import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const Syllabus = () => {
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedSemester, setSelectedSemester] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/syllabus.php');
        const data = await res.json();
        if (Array.isArray(data)) setSyllabi(data);
        else if (data.data) setSyllabi(data.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const uniqueGrades = [...new Set(syllabi.map(s => s.grade).filter(Boolean))];
  const uniqueSubjects = [...new Set(syllabi.map(s => s.subject).filter(Boolean))];
  const uniqueSemesters = [...new Set(syllabi.map(s => s.semester).filter(Boolean))];

  const filtered = syllabi.filter(s => {
    const g = selectedGrade === 'all' || s.grade === selectedGrade;
    const sub = selectedSubject === 'all' || s.subject === selectedSubject;
    const sem = selectedSemester === 'all' || s.semester === selectedSemester;
    return g && sub && sem;
  });

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  const SUBJECT_ICONS = {
    math: 'fa-calculator', mathematics: 'fa-calculator',
    english: 'fa-language', science: 'fa-flask',
    urdu: 'fa-pen-nib', computer: 'fa-laptop',
    islamic: 'fa-moon', quran: 'fa-quran',
    default: 'fa-book',
  };
  const getIcon = (subject) => {
    if (!subject) return SUBJECT_ICONS.default;
    const s = subject.toLowerCase();
    return Object.entries(SUBJECT_ICONS).find(([k]) => s.includes(k))?.[1] || SUBJECT_ICONS.default;
  };

  return (
    <div className="page-enter">
      <Helmet>
        <title>Academic Syllabus — PAK School &amp; College System | Study Plans</title>
        <meta name="description" content="Access comprehensive syllabi for all grades and subjects at PAK School & College System. Download detailed curriculum outlines and study plans." />
        <link rel="canonical" href="https://pakschoolandcollege.com/syllabus" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📖 Academic Planning
          </div>
          <h1>Academic <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Syllabus</span></h1>
          <p>Access comprehensive syllabi for all grades and subjects. Plan your academic journey with confidence.</p>
        </div>
      </div>

      <div className="py-14 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filters */}
          {syllabi.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-5 mb-8 grid sm:grid-cols-3 gap-4">
              {[
                { label: 'Grade', state: selectedGrade, setter: setSelectedGrade, opts: uniqueGrades, format: g => `Grade ${g}` },
                { label: 'Subject', state: selectedSubject, setter: setSelectedSubject, opts: uniqueSubjects, format: s => s },
                { label: 'Semester', state: selectedSemester, setter: setSelectedSemester, opts: uniqueSemesters, format: s => s },
              ].map(({ label, state, setter, opts, format }) => (
                <div key={label}>
                  <label className="block text-navy font-poppins font-600 text-sm mb-2">{label}</label>
                  <select value={state} onChange={e => setter(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                    <option value="all">All {label}s</option>
                    {opts.map(o => <option key={o} value={o}>{format(o)}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}

          {loading && <div className="flex flex-col items-center py-24 gap-4"><div className="spinner" /><p className="text-gray-400 text-sm font-inter">Loading syllabus...</p></div>}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
                <i className="fas fa-book-open text-3xl text-gray-300" />
              </div>
              <h3 className="font-poppins font-700 text-gray-700 text-xl mb-2">No Syllabus Found</h3>
              <p className="text-gray-400 font-inter text-sm">Try adjusting your filters to find what you're looking for.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((syllabus) => (
                <div key={syllabus.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  <div className="h-1.5 bg-gradient-to-r from-purple-500 to-primary" />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                        <i className={`fas ${getIcon(syllabus.subject)} text-purple-600`} />
                      </div>
                      <span className={`text-xs font-poppins font-700 px-2.5 py-1 rounded-full ${syllabus.publish_status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {syllabus.publish_status === 'published' ? 'Available' : 'Coming Soon'}
                      </span>
                    </div>
                    <h3 className="font-poppins font-700 text-navy text-base mb-2">{syllabus.title}</h3>
                    <div className="space-y-1.5 mb-3">
                      {syllabus.grade && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-graduation-cap text-primary w-4 text-center" />Grade {syllabus.grade}</div>}
                      {syllabus.subject && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-book text-purple-500 w-4 text-center" />{syllabus.subject}</div>}
                      {syllabus.semester && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-calendar-alt text-green-500 w-4 text-center" />{syllabus.semester}</div>}
                    </div>
                    {syllabus.description && <p className="text-gray-400 text-xs font-inter leading-relaxed line-clamp-3 mb-4">{syllabus.description}</p>}
                    <div className="flex gap-2">
                      {syllabus.file_path && (
                        <a href={`admin/${syllabus.file_path}`} target="_blank" rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-accent text-white text-xs font-poppins font-600 rounded-xl hover:bg-accent-dark transition-colors">
                          <i className="fas fa-download" />Download
                        </a>
                      )}
                      <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-600 text-white text-xs font-poppins font-600 rounded-xl hover:bg-purple-700 transition-colors">
                        <i className="fas fa-eye" />View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Study Tips */}
          <div className="mt-10 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-lightbulb text-purple-600" />
              </div>
              <div>
                <h4 className="font-poppins font-700 text-purple-900 text-sm mb-3">Study Tips</h4>
                <ul className="text-purple-700 font-inter text-sm space-y-1.5">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />Download and review your syllabus at the beginning of each semester</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />Create a study schedule based on the syllabus timeline</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />Keep track of important dates and deadlines</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />Use the syllabus as a roadmap for your academic progress</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Syllabus;
